<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import V2CatalogFilterChips from './V2CatalogFilterChips.vue';
  import V2CatalogMenuSwitcher from './V2CatalogMenuSwitcher.vue';
  import V2CatalogResultLine from './V2CatalogResultLine.vue';
  import V2CatalogSearchField from './V2CatalogSearchField.vue';

  /**
   * The Menú page's one command bar (plan §5.7): title and summary, search,
   * «Actualizar desde fotos», ⋯ and «+ Nuevo producto»; the filter chips and
   * result line under it. Phones get a compact bar and a find mode; the
   * reorder and selection modes replace it with their own header.
   */
  const props = withDefaults(
    defineProps<{
      /** Nothing to search or filter yet (the empty store). */
      empty?: boolean;
    }>(),
    { empty: false }
  );

  const emit = defineEmits<{ (e: 'close-reorder'): void }>();

  const { state, layout, data, access, url, commands } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const tier = computed(() => layout.tier.value);
  const phone = computed(() => tier.value === 'phone');

  const summary = computed(() =>
    [
      tc('menuPage.summary.products', data.rows.value.length, {
        n: data.rows.value.length,
      }),
      tc('menuPage.summary.categories', data.sections.value.length, {
        n: data.sections.value.length,
      }),
    ].join(' · ')
  );

  const canImport = computed(
    () => access.value.canManage && !data.multipleMenus.value
  );
  /** Outline while the docked editor is open: one primary per view. */
  const newQuiet = computed(
    () => !!state.editor && layout.columns.value.asideDocked
  );
  const selectedCount = computed(() => Object.keys(state.selectedIds).length);
  const showFilters = computed(
    () =>
      !props.empty &&
      (!phone.value || state.mode === 'find' || state.filter !== 'all')
  );
  /** Find mode's first chip: «7 resultados ✕» once something is typed. */
  const resultChip = computed(
    () => state.mode === 'find' && !!data.debouncedQuery.value.trim()
  );

  function openMore(event: MouseEvent) {
    const anchor = event.currentTarget;
    if (anchor instanceof HTMLElement)
      state.panel = { kind: 'moreMenu', targetId: null, anchor };
  }

  function newProduct() {
    url.openNewItem(state.activeCategoryId);
  }

  function enterFind() {
    state.panel = null;
    state.mode = 'find';
  }

  function leaveFind() {
    state.mode = 'browse';
    url.clearSearch();
  }

  function finishSelecting() {
    state.selectedIds = {};
    state.mode = 'browse';
  }
</script>

<template>
  <div class="v2-catalog-command-bar" :class="`tier-${tier}`">
    <!-- Reorder: its own header. -->
    <div v-if="state.mode === 'reorder'" class="bar-row is-mode">
      <h1 class="mode-title">{{ $t('menuPage.reorder.title') }}</h1>
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="close"
        :aria-label="$t('close')"
        :title="$t('close')"
        @click="emit('close-reorder')"
      />
    </div>

    <!-- Selection: «{n} seleccionados · Listo». -->
    <div v-else-if="state.mode === 'select'" class="bar-row is-mode">
      <h1 class="mode-title">
        {{ tc('menuPage.select.count', selectedCount, { n: selectedCount }) }}
      </h1>
      <PosUiButton color="neutral" @click="finishSelecting">{{
        $t('menuPage.select.done')
      }}</PosUiButton>
    </div>

    <!-- Phone find mode: ← [búsqueda]. -->
    <div
      v-else-if="state.mode === 'find'"
      class="bar-row is-find"
      data-tier-only="phone"
    >
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="arrow-left"
        :aria-label="$t('menuPage.search.close')"
        :title="$t('menuPage.search.close')"
        @click="leaveFind"
      />
      <V2CatalogSearchField class="find-field" autofocus />
    </div>

    <div v-else class="bar-row">
      <div class="bar-title">
        <h1 class="title-text">{{ $t('menuPage.title') }}</h1>
        <p v-if="!phone && !empty" class="title-summary">{{ summary }}</p>
        <V2CatalogMenuSwitcher v-if="access.showMenuSwitcher" />
      </div>
      <div class="bar-actions">
        <V2CatalogSearchField
          v-if="tier === 'desktop' && !empty"
          class="bar-search"
          data-tier-only="desktop"
        />
        <PosUiButton
          v-if="phone && !empty"
          class="bar-find"
          color="neutral"
          variant="ghost"
          icon="magnify"
          :aria-label="$t('menuPage.search.placeholder')"
          :title="$t('menuPage.search.placeholder')"
          @click="enterFind"
        />
        <PosUiButton
          v-if="tier === 'desktop' && canImport && !empty"
          class="bar-import"
          color="neutral"
          variant="outline"
          icon="camera-outline"
          @click="commands.runMoreCommand('import')"
          >{{ $t('menuPage.more.import') }}</PosUiButton
        >
        <PosUiButton
          class="bar-more"
          color="neutral"
          variant="ghost"
          icon="dots-horizontal"
          :aria-label="$t('menuPage.more.title')"
          :title="$t('menuPage.more.title')"
          @click="openMore"
        />
        <PosUiButton
          v-if="access.canManage && !empty"
          class="bar-new"
          icon="plus"
          :variant="newQuiet ? 'outline' : 'solid'"
          :color="newQuiet ? 'neutral' : 'primary'"
          @click="newProduct"
          ><span class="new-label">{{ $t('menuPage.newProduct') }}</span
          ><span class="new-label-short">{{
            $t('menuPage.newProductShort')
          }}</span></PosUiButton
        >
      </div>
    </div>

    <div v-if="showFilters && state.mode !== 'reorder'" class="bar-filters">
      <V2CatalogSearchField
        v-if="tier === 'tablet'"
        class="filters-search"
        data-tier-only="tablet"
      />
      <V2CatalogFilterChips
        class="filters-chips"
        :phone="phone"
        :result-chip="resultChip"
      />
    </div>
    <V2CatalogResultLine
      v-if="!phone && data.isFiltering.value && state.mode !== 'reorder'"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-command-bar {
    @apply tw-pb-2;

    .bar-row {
      @apply tw-flex tw-min-h-[52px] tw-items-center;

      &.is-mode {
        @apply tw-justify-between;
      }

      &.is-find .find-field {
        @apply tw-ml-1 tw-min-w-0 tw-flex-1;
      }
    }

    .mode-title {
      @apply tw-m-0 tw-min-w-0 tw-truncate tw-text-xl tw-font-semibold tw-text-neutral-900;
    }

    .bar-title {
      @apply tw-mr-4 tw-min-w-0 tw-flex-1;

      .title-text {
        @apply tw-m-0 tw-truncate tw-text-2xl tw-font-semibold tw-leading-[30px] tw-text-neutral-900;
      }

      .title-summary {
        @apply tw-m-0 tw-truncate tw-text-[13px] tw-leading-5 tw-text-neutral-500;
      }
    }

    // Margins, not flex gap (Chrome < 84).
    .bar-actions {
      @apply tw-flex tw-flex-none tw-items-center;

      > * + * {
        @apply tw-ml-2;
      }

      .bar-search {
        width: 260px;
      }

      .new-label-short {
        @apply tw-hidden;
      }
    }

    .bar-filters {
      @apply tw-mt-2 tw-flex tw-items-center;

      .filters-search {
        @apply tw-mr-3 tw-flex-none;
        width: 220px;
      }

      .filters-chips {
        @apply tw-min-w-0 tw-flex-1;
      }
    }

    // Phones: «Menú · 🔍 · ⋯ · + Nuevo».
    &.tier-phone {
      .bar-title .title-text {
        @apply tw-text-xl;
      }

      .bar-actions > * + * {
        @apply tw-ml-1;
      }

      .bar-actions .new-label {
        @apply tw-hidden;
      }

      .bar-actions .new-label-short {
        @apply tw-inline;
      }

      .bar-filters {
        @apply tw-mt-1;
      }
    }
  }
</style>
